import { RouteKeys, getRoute, useParams } from '../../../routes';

type Params = ReturnType<typeof useParams<Record<string, string | undefined>>>;

/**
 * The list of questionnaires that make up the survey
 * These are used as the routes for the survey slide pages
 * /questionnaireType/:slide
 * We query the backend for the actual questionnaire keys for the active survey
 * for the users organization, and those actual keys (with version) are used to fetch
 * the questionnaire data
 *
 * The route keys here should be stable and not change, even if the actual questionnaire
 * keys change in the backend
 *
 * See surveyContext for mapping of route key -> questionnaire key
 *
 * This will handle versioning to the known questionnaires, but if we need to swap out or add
 * new questionnaires, it will require a code update in this file.
 */
export enum SurveyQuestionnaireRoute {
  DEMOGRAPHICS = 'demographics',
  K6 = 'k6',
  SELF_RATED_HEALTH = 'self-rated-health',
  SOCIAL_NEEDS_SCREENING_TOOL = 'social-needs-screening-tool',
  SOCIAL_NEEDS_SCREENING_FINANCES = 'social-needs-screening-finances',
  SOCIAL_NEEDS_SCREENING_FOOD = 'social-needs-screening-food',
  SOCIAL_NEEDS_SCREENING_PERSONAL_SAFETY = 'social-needs-screening-personal-safety',
  ACADEMIC_STRESS_SCALE = 'academic-stress-scale',
  DROPOUT_RISK_MEASUREMENT = 'dropout-risk-measurement',
  SOSFS = 'sosfs',
}

export const SURVEY_QUESTIONNAIRE_ROUTES = Object.values(SurveyQuestionnaireRoute);

export enum SurveyInterstitialRoute {
  DEMOGRAPHICS = 'demographics',
  MENTAL_PHYSICAL_HEALTH = 'mental-physical-health',
  BASIC_NEEDS = 'basic-needs',
  ACADEMICS = 'academics',
  SOCIAL_FIT = 'social-fit',
}

export const SURVEY_INTERSTITIAL_ROUTES = Object.values(SurveyInterstitialRoute);

export const UNVERSIONED_QUESTIONNAIRE_KEY_TO_ROUTE: Record<string, SurveyQuestionnaireRoute> = {
  'survey-demographics': SurveyQuestionnaireRoute.DEMOGRAPHICS,
  // eslint-disable-next-line id-length
  k6: SurveyQuestionnaireRoute.K6,
  'self-rated-health': SurveyQuestionnaireRoute.SELF_RATED_HEALTH,
  'social-needs-screening-tool': SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_TOOL,
  'social-needs-screening-finances': SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_FINANCES,
  'social-needs-screening-food': SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_FOOD,
  'social-needs-screening-personal-safety':
    SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_PERSONAL_SAFETY,
  'academic-stress-scale': SurveyQuestionnaireRoute.ACADEMIC_STRESS_SCALE,
  'dropout-risk-measurement': SurveyQuestionnaireRoute.DROPOUT_RISK_MEASUREMENT,
  sosfs: SurveyQuestionnaireRoute.SOSFS,
};

export type SurveyQuestionnaireKeyMapping = Record<SurveyQuestionnaireRoute, string>;

type SurveyRoute = {
  routeKey?: SurveyQuestionnaireRoute; // The static route key for questionnaire routes
  isMatch: (routeName: RouteKeys, params: Params) => boolean;
  getNavRoute: () => string;
};

const generateRoute = ({
  routeName,
}: {
  routeName: Extract<RouteKeys, 'surveyStart' | 'surveyFinish'>;
}): SurveyRoute => {
  return {
    isMatch: (currentRouteName: RouteKeys) => routeName === currentRouteName,
    getNavRoute: () => getRoute(routeName, {}),
  };
};

const generateSurveyInterstitialRoute = ({
  interstitialRoute,
}: {
  interstitialRoute: SurveyInterstitialRoute;
}): SurveyRoute => {
  const routeName = 'surveyInterstitials';

  return {
    isMatch: (currentRouteName: RouteKeys, currentParams: Params) => {
      if (currentRouteName !== routeName) return false;
      return currentParams.interstitialRoute === interstitialRoute;
    },
    getNavRoute: () => {
      return getRoute(routeName, { interstitialRoute });
    },
  };
};

const generateSurveyAssessmentRoute = ({
  questionnaireRoute,
}: {
  questionnaireRoute: SurveyQuestionnaireRoute;
}): SurveyRoute => {
  const routeName = 'surveyAssessments';

  return {
    routeKey: questionnaireRoute, // Store the route key for reference
    isMatch: (currentRouteName: RouteKeys, currentParams: Params) => {
      if (currentRouteName !== routeName) return false;
      // URL should always contain the route key (questionnaireType), not the questionnaire key
      return currentParams.questionnaireRoute === questionnaireRoute;
    },
    getNavRoute: () => {
      // Always use the route key (questionnaireRoute) for URL generation
      // The SurveySlidesPage will handle mapping route key -> questionnaire key
      // TODO: To make an improvement where a user can step to the last page of a questionnaire
      // this will need to get updated
      return getRoute(routeName, { questionnaireRoute, slide: '1' });
    },
  };
};

/**
 * This is a fixed order for the Survey with it's questionnaires and interstitials in order
 */
const surveyRoutes = [
  generateRoute({
    routeName: 'surveyStart',
  }),
  generateSurveyInterstitialRoute({
    interstitialRoute: SurveyInterstitialRoute.DEMOGRAPHICS,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.DEMOGRAPHICS,
  }),
  generateSurveyInterstitialRoute({
    interstitialRoute: SurveyInterstitialRoute.MENTAL_PHYSICAL_HEALTH,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.K6,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SELF_RATED_HEALTH,
  }),
  generateSurveyInterstitialRoute({
    interstitialRoute: SurveyInterstitialRoute.BASIC_NEEDS,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_TOOL,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_FINANCES,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_FOOD,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SOCIAL_NEEDS_SCREENING_PERSONAL_SAFETY,
  }),
  generateSurveyInterstitialRoute({
    interstitialRoute: SurveyInterstitialRoute.ACADEMICS,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.ACADEMIC_STRESS_SCALE,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.DROPOUT_RISK_MEASUREMENT,
  }),
  generateSurveyInterstitialRoute({
    interstitialRoute: SurveyInterstitialRoute.SOCIAL_FIT,
  }),
  generateSurveyAssessmentRoute({
    questionnaireRoute: SurveyQuestionnaireRoute.SOSFS,
  }),
  generateRoute({
    routeName: 'surveyFinish',
  }),
] as const;

export const surveyTotalPages = surveyRoutes.length;

// Navigation helpers
export const getSurveyCurrentPageIndex = (routeName: RouteKeys, params: Params): number => {
  return surveyRoutes.findIndex(({ isMatch }) => isMatch(routeName, params));
};

export const getSurveyRouteKeyFromParams = (
  routeName: RouteKeys,
  params: Params,
): SurveyQuestionnaireRoute | undefined => {
  const routeIndex = getSurveyCurrentPageIndex(routeName, params);
  if (routeIndex === -1) return undefined;

  const route = surveyRoutes[routeIndex];
  return route?.routeKey;
};

export const getSurveyPreviousNavRoute = (currentPageIndex: number): string | undefined => {
  const previousPage = surveyRoutes[currentPageIndex - 1];
  if (previousPage === undefined) {
    return undefined;
  }
  return previousPage.getNavRoute();
};

export const getSurveyNextNavRoute = (currentPageIndex: number): string | undefined => {
  const nextPage = surveyRoutes[currentPageIndex + 1];
  if (nextPage === undefined) {
    return undefined;
  }
  return nextPage.getNavRoute();
};
